import React from 'react';
import { Database, Zap, ShieldCheck, RefreshCw, ArrowUpRight, GitBranch } from 'lucide-react';

export const Features: React.FC = () => {
  return (
    <section id="differentiation" className="py-40 px-6 md:px-12 bg-brandBg relative overflow-hidden border-t border-zodiac/5">
      {/* Abstract Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none opacity-[0.02]">
        <div className="absolute top-1/4 right-0 w-[800px] h-[800px] border border-zodiac rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] border border-zodiac rounded-full translate-y-1/2 -translate-x-1/2"></div>
      </div>

      <div className="max-w-7xl mx-auto relative">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-24 gap-8">
          <div className="max-w-2xl">
            <div className="inline-flex items-center space-x-2 mb-6">
              <span className="w-8 h-px bg-accent"></span>
              <p className="text-[10px] font-black uppercase tracking-[0.5em] text-accent">THE DIFFERENTIATOR</p>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-7xl font-bold text-zodiac tracking-tighter leading-[0.95]">
              Built for Decisions,<br />Not Dashboards.
            </h2>
          </div>
          <div className="lg:max-w-sm">
            <p className="text-lg text-zodiac/50 font-light leading-relaxed">
              Most platforms are designed to analyze what already happened. Groflex is designed for the moments when conditions change and action must follow immediately.
            </p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-12 gap-6 lg:gap-8">
          {/* Card 01 - Featured Large */}
          <div className="md:col-span-7 group relative p-10 lg:p-14 bg-zodiac rounded-[2.5rem] text-white overflow-hidden transition-all duration-700 hover:shadow-[0_40px_80px_rgba(17,34,72,0.3)]">
            <div className="absolute top-0 left-0 w-1.5 h-full bg-accent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="absolute top-0 right-0 p-12 text-white/5 font-black text-9xl select-none group-hover:text-accent/10 transition-colors duration-700">01</div>
            <div className="relative z-10">
              <div className="mb-10 p-5 bg-white/10 backdrop-blur-md rounded-2xl w-fit group-hover:bg-accent group-hover:scale-110 transition-all duration-500">
                <Database className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-3xl font-bold mb-6 tracking-tight">Unified Execution Signals <span className="text-accent">(No-Code by Design)</span></h3>
              <p className="text-xl text-white/60 leading-relaxed font-light max-w-xl group-hover:text-white/90 transition-colors">
                Execution-relevant signals are unified into a shared operational context, replacing fragmented data silos with a single, decision-grade source of truth. A no-code logic layer lets business owners adapt thresholds and priorities without engineering dependency.
              </p>
              <div className="mt-12 flex items-center space-x-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <span className="text-xs font-bold uppercase tracking-widest text-accent">Explore Sensing Engine</span>
                <ArrowUpRight className="w-4 h-4 text-accent" />
              </div>
            </div>
            <div className="absolute bottom-0 right-0 w-32 h-32 bg-accent/20 blur-[60px] rounded-full translate-x-1/2 translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
          </div>

          {/* Card 02 - Medium/Small */}
          <div className="md:col-span-5 group relative p-10 bg-white border border-zodiac/5 rounded-[2.5rem] transition-all duration-700 hover:border-accent/30 hover:shadow-2xl hover:shadow-zodiac/5 overflow-hidden">
            <div className="absolute top-0 left-0 w-1.5 h-full bg-accent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="mb-8 p-4 bg-zodiac/5 rounded-2xl w-fit group-hover:bg-accent/10 transition-executive">
              <Zap className="w-7 h-7 text-accent" />
            </div>
            <h3 className="text-2xl font-bold text-zodiac mb-4 tracking-tight">Composable Intelligence</h3>
            <p className="text-base text-zodiac/60 leading-relaxed font-light mb-8">
              Works alongside your existing ERP and operational stack with no rip-and-replace required for immediate value extraction.
            </p>
            <div className="text-[10px] font-black text-zodiac/10 uppercase tracking-[0.4em]">Integrated Stack</div>
          </div>

          {/* Card 03 - Medium/Small */}
          <div className="md:col-span-5 group relative p-10 bg-white border border-zodiac/5 rounded-[2.5rem] transition-all duration-700 hover:border-accent/30 hover:shadow-2xl hover:shadow-zodiac/5 overflow-hidden">
            <div className="absolute top-0 left-0 w-1.5 h-full bg-accent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="mb-8 p-4 bg-zodiac/5 rounded-2xl w-fit group-hover:bg-accent/10 transition-executive">
              <ShieldCheck className="w-7 h-7 text-accent" />
            </div>
            <h3 className="text-2xl font-bold text-zodiac mb-4 tracking-tight">KPI-Driven Governance</h3>
            <p className="text-base text-zodiac/60 leading-relaxed font-light mb-8">
              KPIs are bound to decision rights, execution thresholds, and ownership ensuring actions are governed by the metrics that define success.
            </p>
            <div className="text-[10px] font-black text-zodiac/10 uppercase tracking-[0.4em]">SOX Compliant</div>
          </div>

          {/* Card 04 - Featured Large Light */}
          <div className="md:col-span-7 group relative p-10 lg:p-14 bg-white border border-zodiac/5 rounded-[2.5rem] transition-all duration-700 hover:shadow-2xl hover:border-accent/20 overflow-hidden">
            <div className="absolute top-0 left-0 w-1.5 h-full bg-accent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="absolute top-0 right-0 p-12 text-zodiac/5 font-black text-9xl select-none group-hover:text-accent/5 transition-colors duration-700">04</div>
            <div className="relative z-10">
              <div className="mb-10 p-5 bg-brandBg shadow-lg rounded-2xl w-fit group-hover:bg-accent group-hover:text-white transition-all duration-500">
                <RefreshCw className="w-8 h-8 text-accent group-hover:text-white" />
              </div>
              <h3 className="text-3xl font-bold text-zodiac mb-6 tracking-tight">Closed-Loop Learning</h3>
              <p className="text-xl text-zodiac/60 leading-relaxed font-light max-w-md">
                Outcomes, overrides, and timing data are fed back into the engine, continuously improving the quality of future autonomous decisions.
              </p>
              <div className="mt-12 flex items-center space-x-2 text-zodiac font-bold text-[11px] uppercase tracking-widest">
                <span className="w-2 h-2 rounded-full bg-accent animate-pulse"></span>
                <span>Active Model Improvement</span>
              </div>
            </div>
          </div>

          {/* Card 05 - Full Width (Standardized Design) */}
          <div className="md:col-span-12 group relative p-10 lg:p-14 bg-zodiac rounded-[2.5rem] text-white overflow-hidden transition-all duration-700 hover:shadow-[0_40px_80px_rgba(17,34,72,0.3)]">
            <div className="absolute top-0 left-0 w-1.5 h-full bg-accent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="absolute top-0 right-0 p-12 text-white/5 font-black text-9xl select-none group-hover:text-accent/10 transition-colors duration-700">05</div>
            <div className="relative z-10">
              <div className="mb-10 p-5 bg-white/10 backdrop-blur-md rounded-2xl w-fit group-hover:bg-accent group-hover:scale-110 transition-all duration-500">
                <GitBranch className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-3xl font-bold mb-6 tracking-tight">Synthetic Scenario Planning</h3>
              <p className="text-xl text-white/60 leading-relaxed font-light max-w-3xl group-hover:text-white/90 transition-colors">
                Synthetic scenarios simulate execution paths and stress-test decisions before action is taken. By modeling alternative paths in a deterministic environment, teams can anticipate downstream effects and coordinate complex shifts across the operational stack without real-world risk.
              </p>
              <div className="mt-12 flex items-center space-x-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <span className="text-xs font-bold uppercase tracking-widest text-accent">Initialize Sandbox</span>
                <ArrowUpRight className="w-4 h-4 text-accent" />
              </div>
            </div>
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-accent/10 blur-[100px] rounded-full translate-x-1/2 translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-1000"></div>
          </div>
        </div>
      </div>
    </section>
  );
};